import React, { useMemo, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { animate } from "framer-motion";
import {
  BarChart3,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Gauge,
  Search,
  GitBranch,
  Compass,
} from "lucide-react";
import Typography from "./ui/Typography";
import Button from "./ui/Button";
import Chip from "./ui/Chip";
import SlideOutPanel from "./ui/SlideOutPanel";
import SectionCard from "./ui/SectionCard";
import BridgeText from "./BridgeText";
import { cn } from "../utils/classNames";
import { handleInternalLinkClick } from "../utils/navigation";
import { bridgePreviewText } from "../utils/bridgeText";

const AnimatedNumber = ({ value = 0, precision = 0 }) => {
  const normalizedValue = value || 0;
  const [display, setDisplay] = useState(normalizedValue);
  const previousValueRef = useRef(normalizedValue);

  useEffect(() => {
    const start = previousValueRef.current;
    const end = normalizedValue;
    if (start === end) {
      previousValueRef.current = end;
      return undefined;
    }

    const controls = animate(start, end, {
      duration: 0.5,
      ease: "easeOut",
      onUpdate: (latest) => {
        setDisplay(latest);
      },
    });
    previousValueRef.current = end;

    return () => {
      controls.stop();
    };
  }, [normalizedValue]);

  const formatted = precision
    ? Number(display).toFixed(precision)
    : Math.round(display).toLocaleString();

  return <span>{formatted}</span>;
};

AnimatedNumber.propTypes = {
  value: PropTypes.number,
  precision: PropTypes.number,
};

const TrendBadge = ({ trend }) => {
  if (!trend || !Number.isFinite(trend.delta) || trend.direction === "flat" || trend.delta === 0) {
    return null;
  }

  const isUp = trend.direction === "up";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-1 text-body-xs text-label border",
        isUp ? "bg-success/20 text-success border-success" : "bg-destructive/20 text-destructive border-destructive"
      )}
    >
      {isUp ? (
        <ArrowUpRight className="h-3 w-3" />
      ) : (
        <ArrowDownRight className="h-3 w-3" />
      )}
      {`${isUp ? "+" : ""}${trend.delta}`}
      {typeof trend.percent === "number" && (
        <span className="text-micro opacity-70">{`(${trend.percent}%)`}</span>
      )}
    </span>
  );
};

TrendBadge.propTypes = {
  trend: PropTypes.shape({
    delta: PropTypes.number,
    percent: PropTypes.number,
    direction: PropTypes.string,
  }),
};

const TaskRow = ({ label, description, action = null, divided = true }) => (
  <div className={cn("py-3 last:pb-0", divided && "border-t border-insight-border")}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Typography variant="caption" className="text-primary">{label}</Typography>
        <Typography variant="body-small" className="mt-1 text-muted-foreground">
          {description}
        </Typography>
      </div>
      {action}
    </div>
  </div>
);

TaskRow.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  action: PropTypes.node,
  divided: PropTypes.bool,
};


const InsightsPanel = ({
  open,
  onClose,
  insights = null,
  count = 0,
  totalCount = 0,
  candidateCount = null,
  relativeUpdated = null,
  onGenreToggle = null,
  onNetworkToggle = null,
  selectedGenres = [],
  selectedNetworks = [],
  onResetFilters = null,
  windowLabel = null,
  recommendationPairings = [],
}) => {
  const parseLocalDate = (value) => {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return null;
    }
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  };

  const {
    genreBuckets,
    networkBuckets,
    ratingSummary,
    ratingBuckets,
    topRatedUpcoming,
    trends,
  } = useMemo(() => {
    if (!insights) {
      return {
        newVsReturning: { total: 0, newCount: 0, returningCount: 0, percentageNew: 0, percentageReturning: 0 },
        genreBuckets: [],
        networkBuckets: [],
        ratingSummary: {
          count: 0,
          ratedCount: 0,
          unratedCount: 0,
          average: null,
          median: null,
          min: null,
          max: null,
          distribution: { "9+": 0, "8-8.9": 0, "7-7.9": 0, "<7": 0, unrated: 0 },
        },
        ratingBuckets: [],
        dailyVolume: [],
        topRatedUpcoming: [],
        sourceBreakdown: [],
        trends: null,
      };
    }
    return insights;
  }, [insights]);

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat("en-GB", { month: "short", day: "numeric" }),
    []
  );
  const pairingCount = Array.isArray(recommendationPairings) ? recommendationPairings.length : 0;
  const featuredPairing = pairingCount ? recommendationPairings[0] : null;
  const featuredPairingBridgePreview = featuredPairing
    ? bridgePreviewText(
        featuredPairing.bridge,
        featuredPairing.anchor?.title || "",
        featuredPairing.current?.title || ""
      )
    : "";
  const ratedCount = ratingSummary?.ratedCount ?? 0;
  const unratedCount = ratingSummary?.unratedCount ?? 0;
  const averageRating = ratingSummary?.average ? ratingSummary.average.toFixed(1) : "—";
  const topRatedShow = topRatedUpcoming?.[0] || null;
  const strongestBucket = ratingBuckets.reduce(
    (current, bucket) => (bucket.value > current.value ? bucket : current),
    { label: null, value: 0 }
  );
  const hasActiveFilters = Boolean(selectedGenres?.length || selectedNetworks?.length);

  const headerSummary = (
    <>
      <div className="mt-3 w-full border border-insight-border bg-insight-screen text-foreground shadow-insight-inset rounded-lg overflow-hidden relative sm:hidden">
        <div className="absolute inset-0 hero-atom-grain pointer-events-none" />
        <div className="relative z-10 grid grid-cols-3 gap-2 p-3">
          <div>
            <span className="text-micro text-muted-foreground text-label">Shortlist</span>
            <Typography variant="h4" className="text-foreground font-bold tracking-tight">
              <AnimatedNumber value={count} />
            </Typography>
          </div>
          <div>
            <span className="text-micro text-muted-foreground text-label">Recommendations</span>
            <Typography variant="h4" className="text-foreground font-bold tracking-tight">
              <AnimatedNumber value={pairingCount} />
            </Typography>
          </div>
          <div>
            <span className="text-micro text-muted-foreground text-label">Window</span>
            <Typography variant="caption" className="block pt-1 text-foreground text-label">
              {windowLabel?.description ?? "Now"}
            </Typography>
          </div>
        </div>
      </div>

      <div className="mt-4 hidden w-full border border-insight-border bg-insight-screen text-foreground shadow-insight-inset rounded-lg overflow-hidden relative sm:block">
        <div className="absolute inset-0 hero-atom-grain pointer-events-none" />
        <div className="relative z-10 grid grid-cols-2 gap-3 p-4">
          <div className="space-y-2">
            <span className="text-caption text-muted-foreground text-label">Shortlist</span>
            <div className="flex items-baseline gap-2">
              <Typography variant="h2" className="text-foreground font-bold tracking-tight">
                <AnimatedNumber value={count} />
              </Typography>
              <TrendBadge trend={trends?.premiumCount} />
            </div>
            <span className="text-caption text-muted-foreground text-label-light">
              {candidateCount ?? totalCount ?? count} considered.
            </span>
          </div>
          <div className="space-y-2">
            <span className="text-caption text-muted-foreground text-label">Recommendations</span>
            <Typography variant="h2" className="text-foreground font-bold tracking-tight">
              <AnimatedNumber value={pairingCount} />
            </Typography>
            <span className="text-caption text-muted-foreground text-label-light">
              Current shows, each paired with one you may already know
            </span>
          </div>
          <div className="col-span-2 flex flex-wrap items-center gap-2 text-caption text-muted-foreground text-label-light">
            <span className="border border-insight-border px-3 py-1 font-bold text-foreground">
              Window: {windowLabel?.range ?? "—"}
            </span>
            {windowLabel?.description && (
              <span className="border border-insight-border px-3 py-1">
                {windowLabel.description}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <SlideOutPanel
      open={open}
      onClose={onClose}
      title="Insights"
      subtitle={`Refreshed ${relativeUpdated || "recently"}`}
      icon={BarChart3}
      headerContent={headerSummary}
      ariaLabel="Current lineup insights"
    >
      <SectionCard title="Tune this lineup" icon={Compass} delay={0.05}>
        <TaskRow
          label="Refine current shows"
          description="Narrow this 30-day window by genre or platform without leaving the homepage."
          divided={false}
        />
        <TaskRow
          label="Follow recommendations"
          description={`${pairingCount} current ${pairingCount === 1 ? "pick is" : "picks are"} linked to familiar shows with a reason why.`}
          action={(
            <Button variant="ghost" size="sm" asChild className="hidden shrink-0 sm:inline-flex">
              <a
                href="/most-recommended"
                onClick={(event) => handleInternalLinkClick(event, "/most-recommended")}
              >
                See recommendations
              </a>
            </Button>
          )}
        />
        <TaskRow
          label="Search older months"
          description="Use the Archive page when you want back-catalogue finds by mood, setup, title, or platform."
          action={(
            <Button variant="ghost" size="sm" asChild className="hidden shrink-0 sm:inline-flex">
              <a href="/archive" onClick={(event) => handleInternalLinkClick(event, "/archive")}>
                Archive
              </a>
            </Button>
          )}
        />
      </SectionCard>

      {featuredPairing && (
        <SectionCard title="A recommendation to try" icon={GitBranch} delay={0.08}>
          <div className="space-y-4">
            <div className="rounded-md border border-insight-border bg-insight p-4">
              <Typography variant="caption" className="text-muted-foreground">
                If you liked {featuredPairing.anchor?.title || "a familiar show"}
              </Typography>
              <Typography variant="h4" className="mt-1 text-foreground">
                Try {featuredPairing.current?.title || "one of this window's picks"}
              </Typography>
              {featuredPairingBridgePreview && (
                <Typography variant="body-small" className="mt-2 text-muted-foreground">
                  <BridgeText
                    text={featuredPairingBridgePreview}
                    titles={[featuredPairing.anchor?.title || "", featuredPairing.current?.title || ""]}
                  />
                </Typography>
              )}
            </div>
            <Button variant="wireframe" size="sm" asChild>
              <a
                href="/most-recommended"
                onClick={(event) => handleInternalLinkClick(event, "/most-recommended")}
              >
                See more recommendations
              </a>
            </Button>
          </div>
        </SectionCard>
      )}

      <SectionCard title="Quality notes" icon={BarChart3} delay={0.1}>
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4 border-b border-insight-border pb-3">
            <div>
              <Typography variant="caption" className="text-primary">Rated shows</Typography>
              <Typography variant="body-small" className="mt-1 text-muted-foreground">
                {ratedCount} of {ratedCount + unratedCount} current picks have a rating.
                {strongestBucket.label && strongestBucket.value > 0
                  ? ` Most rated titles sit in the ${strongestBucket.label} band.`
                  : ""}
              </Typography>
            </div>
            <Typography variant="h3" className="shrink-0 text-foreground">
              {averageRating}
            </Typography>
          </div>
          {topRatedShow && (
            <div>
              <Typography variant="caption" className="text-primary">Highest-rated pick</Typography>
              <Typography variant="body" className="mt-1 font-semibold text-foreground">
                {topRatedShow.title}
                {typeof topRatedShow.rating === "number" ? `, ${topRatedShow.rating.toFixed(1)}` : ""}
              </Typography>
              <Typography variant="caption" className="text-muted-foreground text-label-light">
                {topRatedShow.releaseDate && parseLocalDate(topRatedShow.releaseDate)
                  ? dateFormatter.format(parseLocalDate(topRatedShow.releaseDate))
                  : "Date TBA"}
                {topRatedShow.network ? ` • ${topRatedShow.network}` : ""}
              </Typography>
            </div>
          )}
        </div>
      </SectionCard>

      {genreBuckets && genreBuckets.length > 0 && (
        <SectionCard title="Filter by genre" icon={Filter} delay={0.12}>
          <p className="mb-3 text-caption text-muted-foreground">
            Tap a genre to narrow the current lineup. Use archive search when you want older shows with a similar feel.
          </p>
          <div className="flex flex-wrap gap-2">
            {genreBuckets.slice(0, 10).map((genre) => {
              const isActive = selectedGenres?.some(
                (item) => item.toLowerCase() === genre.label.toLowerCase()
              );
              const percentageLabel = `${Math.round((genre.percentage || 0) * 100)}%`;
              return (
                <button
                  key={genre.label}
                  type="button"
                  onClick={() => onGenreToggle?.(genre.label)}
                  className="focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Chip
                    variant={isActive ? "primary" : "outline"}
                    className={cn(
                      isActive
                        ? "shadow-badge"
                        : "hover:border-primary"
                    )}
                  >
                    <span className="font-medium">{genre.label}</span>
                    <span className="ml-2 text-muted-foreground">{percentageLabel}</span>
                  </Chip>
                </button>
              );
            })}
          </div>
          {selectedGenres?.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="mt-4"
            >
              Reset filters
            </Button>
          )}
        </SectionCard>
      )}

      {networkBuckets && networkBuckets.length > 0 && (
        <SectionCard title="Filter by platform" icon={Gauge} delay={0.16}>
          <div className="grid grid-cols-1 gap-3">
            {networkBuckets.slice(0, 6).map((network) => {
              const isActive = selectedNetworks?.some(
                (item) => item.toLowerCase() === network.label.toLowerCase()
              );
              const percentageLabel = `${Math.round((network.percentage || 0) * 100)}%`;
              return (
                <button
                  key={network.label}
                  type="button"
                  onClick={() => onNetworkToggle?.(network.label)}
                  className={cn(
                    "flex items-center justify-between border px-4 py-3 text-left rounded-md",
                    "bg-insight text-foreground transition-shadow shadow-none",
                    isActive ? "border-primary shadow-insight-inset" : "border-insight-border hover:border-primary"
                  )}
                >
                  <div>
                    <Typography variant="body" className="font-semibold text-foreground">
                      {network.label}
                    </Typography>
                    <Typography variant="caption" className="text-muted-foreground">
                      {percentageLabel} of lineup
                    </Typography>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center border border-insight-border bg-insight rounded-md text-badge-count text-body">
                    {network.count}
                  </div>
                </button>
              );
            })}
          </div>
          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="mt-4"
            >
              Reset filters
            </Button>
          )}
        </SectionCard>
      )}

      <SectionCard title="When to use the archive" icon={Search} delay={0.22}>
        <div className="space-y-4">
          <Typography variant="body-small" className="text-muted-foreground">
            The homepage is for current decisions. The archive is for back-catalogue discovery: describe a mood, setup, actor-adjacent memory, genre blend, or platform and search completed months.
          </Typography>
          <div className="flex flex-wrap gap-2">
            <Button variant="wireframe" size="sm" asChild>
              <a href="/archive" onClick={(event) => handleInternalLinkClick(event, "/archive")}>
                Search the archive
              </a>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a href="/most-recommended" onClick={(event) => handleInternalLinkClick(event, "/most-recommended")}>
                Recommendations
              </a>
            </Button>
          </div>
        </div>
      </SectionCard>

      <div className="border border-insight-border bg-insight-screen text-foreground shadow-insight-inset rounded-lg overflow-hidden relative">
        <div className="absolute inset-0 hero-atom-grain pointer-events-none" />
        <div className="relative z-10 flex flex-col gap-3 p-4">
          <p className="text-caption text-muted-foreground">
            Tip: this drawer changes the current homepage filters. Archive overview lives on the Archive page and covers long-term trends.
          </p>
          <Button
            type="button"
            variant="space"
            size="sm"
            onClick={onResetFilters}
            className="self-start"
          >
            Reset filters
          </Button>
        </div>
      </div>
    </SlideOutPanel>
  );
};

InsightsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  insights: PropTypes.shape({
    newVsReturning: PropTypes.shape({
      total: PropTypes.number,
      newCount: PropTypes.number,
      returningCount: PropTypes.number,
      percentageNew: PropTypes.number,
      percentageReturning: PropTypes.number,
    }),
    genreBuckets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        percentage: PropTypes.number,
      })
    ),
    networkBuckets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        percentage: PropTypes.number,
      })
    ),
    ratingSummary: PropTypes.shape({
      count: PropTypes.number,
      ratedCount: PropTypes.number,
      unratedCount: PropTypes.number,
      average: PropTypes.number,
      median: PropTypes.number,
      min: PropTypes.number,
      max: PropTypes.number,
      distribution: PropTypes.object,
    }),
    ratingBuckets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        percentage: PropTypes.number,
      })
    ),
    dailyVolume: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        dayLabel: PropTypes.string,
        count: PropTypes.number,
      })
    ),
    topRatedUpcoming: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        slug: PropTypes.string,
        title: PropTypes.string,
        rating: PropTypes.number,
        releaseDate: PropTypes.string,
        network: PropTypes.string,
      })
    ),
    sourceBreakdown: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
        percentage: PropTypes.number,
      })
    ),
    trends: PropTypes.shape({
      totalCount: PropTypes.object,
      premiumCount: PropTypes.object,
    }),
  }),
  count: PropTypes.number,
  totalCount: PropTypes.number,
  candidateCount: PropTypes.number,
  relativeUpdated: PropTypes.string,
  onGenreToggle: PropTypes.func,
  onNetworkToggle: PropTypes.func,
  selectedGenres: PropTypes.arrayOf(PropTypes.string),
  selectedNetworks: PropTypes.arrayOf(PropTypes.string),
  onResetFilters: PropTypes.func,
  recommendationPairings: PropTypes.arrayOf(
    PropTypes.shape({
      anchor: PropTypes.shape({
        title: PropTypes.string,
      }),
      current: PropTypes.shape({
        title: PropTypes.string,
      }),
      bridge: PropTypes.string,
    })
  ),
  windowLabel: PropTypes.shape({
    range: PropTypes.string,
    description: PropTypes.string,
  }),
};

export default InsightsPanel;
